import { useState, useCallback } from 'react';
import { useClearStaleEmrAttemptWhenPushFailedReleased } from './useClearStaleEmrAttemptWhenPushFailedReleased';
import type { UseFormGetValues, UseFormTrigger } from 'react-hook-form';
import type { HnpUploadTab } from '../../services/api/types.gen';
import { getTodayInPT } from '../../utils/dateUtils';
import { getReferralDocs } from '../../services/api';
import { onboardPatientKinnserPatientOnboardClinicIdBranchIdPost } from '../../services/api/sdk.gen';
import {
  extractDetailMessage,
  getResponseDetail,
  getSurfaceableError,
  parseFieldErrorsFromDetail,
  type FieldValidationError,
} from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { resolveClinicKeyFromAgencyClinicId } from '../../utils/clinicKeyUtils';
import { useStore } from '../../store';

export interface KinnserAttachment {
  client_file: string | null;
  server_file: string | null;
  error: string;
}

export type KinnserOnboardValidationError = FieldValidationError;

export interface KinnserOnboardPatientResponse {
  patient_creation: {
    workflow_id: string;
    success: boolean;
    message: string;
  };
  task_creation: {
    patient_task_key: string;
    task_name: string;
  };
  upload: {
    attachments: KinnserAttachment[];
    patient_task_key: string;
  };
  // Per-field validation errors from the backend (HTTP 400 with detail.errors).
  // Used to render clickable bullets that jump to the offending field.
  validation_errors?: KinnserOnboardValidationError[];
  // Set when the failure carries a surfaceable business error (HTTP 409). The
  // display gates generic-message suppression on this so the real message shows.
  surfaceable?: boolean;
  error_code?: string;
}

/**
 * Build a "push to EMR failed" response envelope, keeping placeholder values
 * for the fields we never got a chance to populate. Used by every error path
 * so the UI can render a consistent failure state.
 */
function buildFailedOnboardResult(args: {
  message: string;
  taskName: string;
  validationErrors?: KinnserOnboardValidationError[];
  surfaceable?: boolean;
  errorCode?: string;
}): KinnserOnboardPatientResponse {
  const { message, taskName, validationErrors, surfaceable, errorCode } = args;
  return {
    patient_creation: { workflow_id: '', success: false, message },
    task_creation: { patient_task_key: '', task_name: taskName },
    upload: { attachments: [], patient_task_key: '' },
    ...(validationErrors ? { validation_errors: validationErrors } : {}),
    ...(surfaceable ? { surfaceable: true, error_code: errorCode } : {}),
  };
}

export interface S3File {
  url: string;
  filename: string;
}

interface ReferralDocument {
  id: string;
  file_name: string;
  url: string;
}

interface UseKinnserOnboardingOptions<
  T extends {
    agency_clinic_id?: string | null;
    kinnser_branch_id?: string | null;
  },
> {
  intakeId: string | undefined;
  /** Global intake status; used to clear stale client errors after push_failed is reset. */
  intakeStatus?: string | null;
  trigger: UseFormTrigger<T>;
  getValues: UseFormGetValues<T>;
  tabName: HnpUploadTab | null | undefined;
  taskName: string | null | undefined;
  s3Files: S3File[];
  // Optional task/upload configuration
  taskDate?: string; // YYYY-MM-DD format
  patientLocation?: string;
}

interface UseKinnserOnboardingReturn {
  onboardingInKinnser: boolean;
  onboardResult: KinnserOnboardPatientResponse | null;
  handleOnboardInKinnser: () => Promise<void>;
}

/**
 * Hook for onboarding patients in Kinnser (creates patient, task, and uploads attachment).
 * Manages the onboarding state and handles the API call.
 */
export function useKinnserOnboarding<
  T extends {
    agency_clinic_id?: string | null;
    kinnser_branch_id?: string | null;
  },
>({
  intakeId,
  trigger,
  getValues,
  tabName,
  taskName,
  s3Files,
  taskDate,
  patientLocation,
  intakeStatus,
}: UseKinnserOnboardingOptions<T>): UseKinnserOnboardingReturn {
  const [creatingInKinnser, setOnboardingInKinnser] = useState(false);
  const [onboardResult, setOnboardResult] =
    useState<KinnserOnboardPatientResponse | null>(null);

  const clearOnboardResult = useCallback(() => {
    setOnboardResult(null);
  }, []);
  useClearStaleEmrAttemptWhenPushFailedReleased(
    intakeId,
    intakeStatus,
    clearOnboardResult
  );

  const handleOnboardInKinnser = useCallback(async () => {
    if (!intakeId) return;

    // Validate form before calling API
    const isValid = await trigger();
    if (!isValid) {
      return;
    }

    // Validate that if any H&P field is specified, all must be specified
    const tabNameProvided = tabName !== null && tabName !== undefined;
    const taskNameProvided =
      taskName !== null && taskName !== undefined && taskName.trim() !== '';
    const taskDateProvided =
      taskDate !== null && taskDate !== undefined && taskDate.trim() !== '';

    // If tab_name and task_name are provided but task_date is not, default to today
    let taskDateValue: string | null = taskDate || null;
    if (tabNameProvided && taskNameProvided && !taskDateProvided) {
      taskDateValue = getTodayInPT(); // YYYY-MM-DD format in Pacific Time
    }

    // Count how many fields are provided (after defaulting task_date if needed)
    const finalTaskDateProvided = taskDateValue !== null;
    const providedCount = [
      tabNameProvided,
      taskNameProvided,
      finalTaskDateProvided,
    ].filter(Boolean).length;

    // If any field is provided, all must be provided
    if (providedCount > 0 && providedCount < 3) {
      setOnboardResult(
        buildFailedOnboardResult({
          message:
            'If any of H&P upload tab, H&P upload task, or task date is specified, all must be specified.',
          taskName: taskName ?? '',
        })
      );
      return;
    }

    // Get clinic_id and branch_id from form values
    const formValues = getValues();
    const agencyClinicId = formValues.agency_clinic_id;
    const branchId = formValues.kinnser_branch_id;

    if (!agencyClinicId || !branchId) {
      console.error('Clinic ID and Branch ID are required for onboarding');
      setOnboardResult(
        buildFailedOnboardResult({
          message: 'Clinic ID and Branch ID are required',
          taskName: taskName ?? '',
        })
      );
      return;
    }

    // Resolve Kinnser clinic_key from the agency_clinic_id (UUID) stored in the form
    const agencyClinics = useStore.getState().agencyClinics;
    const clinicKey = resolveClinicKeyFromAgencyClinicId(
      agencyClinics,
      agencyClinicId
    );

    if (!clinicKey) {
      console.error(
        'Could not resolve Kinnser clinic_key for agency_clinic_id:',
        agencyClinicId
      );
      setOnboardResult(
        buildFailedOnboardResult({
          message:
            'Could not resolve Kinnser clinic key. Please ensure the selected clinic has a valid Kinnser configuration.',
          taskName: taskName ?? '',
        })
      );
      return;
    }

    const clinicIdNum = parseInt(clinicKey, 10);
    const branchIdNum = parseInt(branchId, 10);
    if (Number.isNaN(clinicIdNum) || Number.isNaN(branchIdNum)) {
      console.error(
        'Invalid Kinnser clinic or branch id for onboard path:',
        clinicKey,
        branchId
      );
      setOnboardResult(
        buildFailedOnboardResult({
          message: 'Clinic ID and Branch ID are required',
          taskName: taskName ?? '',
        })
      );
      return;
    }

    setOnboardingInKinnser(true);
    setOnboardResult(null);

    try {
      // Validate that we have at least one file
      if (!s3Files || s3Files.length === 0) {
        setOnboardResult(
          buildFailedOnboardResult({
            message: 'At least one document is required for onboarding',
            taskName: taskName ?? '',
          })
        );
        setOnboardingInKinnser(false);
        return;
      }

      // Regenerate presigned URLs to ensure they haven't expired
      // This prevents 403 errors when the user has been on the page for a long time
      let freshS3Files: S3File[] = [];
      try {
        const urlResponse = await getReferralDocs({
          path: { intake_id: intakeId },
        });
        const documents = urlResponse.data?.documents;
        if (documents && Array.isArray(documents) && documents.length > 0) {
          freshS3Files = (documents as unknown as ReferralDocument[]).map(
            doc => ({
              url: doc.url,
              filename: doc.file_name,
            })
          );
        } else {
          console.error('Failed to get fresh presigned URLs');
          setOnboardResult(
            buildFailedOnboardResult({
              message: 'Failed to generate presigned URLs for documents',
              taskName: taskName ?? '',
            })
          );
          setOnboardingInKinnser(false);
          return;
        }
      } catch (urlError) {
        console.error('Error fetching fresh presigned URLs:', urlError);
        setOnboardResult(
          buildFailedOnboardResult({
            message: 'Failed to generate presigned URLs for documents',
            taskName: taskName ?? '',
          })
        );
        setOnboardingInKinnser(false);
        return;
      }

      const response =
        await onboardPatientKinnserPatientOnboardClinicIdBranchIdPost({
          path: { clinic_id: clinicIdNum, branch_id: branchIdNum },
          body: {
            intake_id: intakeId,
            tab_name: tabName || null,
            task_name: taskName || null,
            task_date: taskDateValue || null,
            patient_location: patientLocation || null,
            s3_files: freshS3Files,
          },
          throwOnError: true,
        });

      if (response.data) {
        setOnboardResult(response.data as KinnserOnboardPatientResponse);
      }
    } catch (error) {
      console.error('Error onboarding patient in Kinnser:', error);

      // Surfaceable business errors (HTTP 409) take priority and are shown verbatim.
      const surfaceable = getSurfaceableError(error);
      if (surfaceable) {
        setOnboardResult(
          buildFailedOnboardResult({
            message: surfaceable.message,
            taskName: taskName ?? '',
            surfaceable: true,
            errorCode: surfaceable.errorCode,
          })
        );
        return;
      }

      const status = getHttpStatus(error);
      const is4xx = status !== undefined && status >= 400 && status < 500;
      const detail = getResponseDetail(error);

      const validationErrors = is4xx
        ? parseFieldErrorsFromDetail(detail)
        : undefined;
      if (validationErrors && validationErrors.length > 0) {
        setOnboardResult(
          buildFailedOnboardResult({
            message:
              'Some required fields are missing or invalid. Update them and try again.',
            taskName: taskName ?? '',
            validationErrors,
          })
        );
        return;
      }

      const detailMessage = is4xx ? extractDetailMessage(detail) : null;
      if (detailMessage) {
        setOnboardResult(
          buildFailedOnboardResult({
            message: detailMessage,
            taskName: taskName ?? '',
          })
        );
        return;
      }

      setOnboardResult(
        buildFailedOnboardResult({
          message: 'Failed to onboard patient. Please try again.',
          taskName: taskName ?? '',
        })
      );
    } finally {
      setOnboardingInKinnser(false);
    }
  }, [
    intakeId,
    trigger,
    getValues,
    tabName,
    taskName,
    taskDate,
    patientLocation,
    s3Files,
  ]);

  return {
    onboardingInKinnser: creatingInKinnser,
    onboardResult: onboardResult,
    handleOnboardInKinnser: handleOnboardInKinnser,
  };
}
